import React, { FC, useState } from 'react';
import useUpdateOrganizationSetting from '../../../hooks/api/organization/useUpdateOrganizationSetting';
import useDeleteOrganizationSetting from '../../../hooks/api/organization/useDeleteOrganizationSetting';
import ModalComponent from '../../../components/modal/ModalComponent';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'react-bootstrap';
import PaymentFee from './PaymentFee';
import DefaultSetting from './DefaultSetting';
import { OrganizationSetting } from '../../../services/organization/OrganizationService';

interface SettingProps {
  setting: OrganizationSetting;
  onRefresh?: () => void;
}

const OrganizationSettingComponent = {
  PAYMENTFEE: PaymentFee,
  DEFAULT: DefaultSetting,
};

const Setting: FC<SettingProps> = ({ setting, onRefresh }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState(setting.content);
  const { updateOrganizationSetting } = useUpdateOrganizationSetting();
  const { deleteOrganizationSetting } = useDeleteOrganizationSetting();
  const intl = useIntl();

  const save = async () => {
    await updateOrganizationSetting(setting.organizationId, { ...setting, content: editedContent });
    onRefresh?.();
    setEditMode(false);
  };

  const SettingComponent = OrganizationSettingComponent[setting.type] || DefaultSetting;

  return (
    <div className="setting-container">
      <div className="d-flex">
        <div className="me-3 setting-type">{setting.type}</div>
        <div className="setting-content d-flex align-items-center">
          <SettingComponent content={setting.content} editMode={editMode} onChange={setEditedContent} />
          {!editMode && (
            <>
              <Button variant="primary" className="me-1 ms-3" onClick={() => setEditMode(true)}>
                <span className="mdi mdi-pencil"></span>
              </Button>
              <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
                <span className="mdi mdi-trash-can"></span>
              </Button>
            </>
          )}
          {editMode && (
            <>
              <Button variant="primary" className="me-1 ms-3" onClick={() => save()}>
                <span className="mdi mdi-content-save"></span>
              </Button>
              <Button variant="primary" onClick={() => setEditMode(false)}>
                <span className="mdi mdi-close"></span>
              </Button>
            </>
          )}
        </div>
      </div>
      <ModalComponent
        modalHeading={intl.formatMessage({ id: 'organizationPage.organizationSettings.deleteSettingTitle' })}
        modalSize={'lg'}
        show={showDeleteConfirm}
        buttons={[
          {
            text: intl.formatMessage({ id: 'cancel' }),
            buttonVariant: 'secondary',
            onClick: () => setShowDeleteConfirm(false),
          },
          {
            text: intl.formatMessage({ id: 'delete' }),
            buttonVariant: 'primary',
            onClick: async () => {
              if (setting.id) {
                await deleteOrganizationSetting(setting.organizationId, setting.id);
                setShowDeleteConfirm(false);
                onRefresh?.();
              }
            },
          },
        ]}
        closeSettings={() => setShowDeleteConfirm(false)}
      >
        <FormattedMessage id="organizationPage.organizationSettings.deleteSettingMessage"></FormattedMessage>
      </ModalComponent>
    </div>
  );
};

export default Setting;
